import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import './PrivacyPolicy.css';
import Footer from '../Home/Footer';

const PrivacyPolicy = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy | Cloud4CoolKids";
    
    if (isInView) {
      controls.start("visible");
    }

    const handlePopState = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.5
      }
    },
    hover: {
      scale: 1.01,
      transition: { duration: 0.2 }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8 }
    }
  };

  const sections = [
    {
      title: "Introduction",
      content: [
        "Welcome to Cloud4CoolKids ('we,' 'our,' or 'us'). We are committed to protecting your privacy and the privacy of the users of our online courses platform. This Privacy Policy outlines how we collect, use, disclose, and protect your personal information when you use our platform."
      ]
    },
    {
      title: "Information You Provide",
      content: [
        "We may collect personal information that you voluntarily provide when you use our online courses platform, including:",
        "• Registration Information: Name, email address, username, password, and other account details.",
        "• Profile Information: Information you provide in your user profile, such as your bio, profile picture, and other optional details.",
        "• Course-Related Information: Information you submit or generate while participating in online courses, such as quiz scores, assignments, discussions, and interactions with instructors and other learners.",
        "• Payment Information: If applicable, payment details for course enrollment, such as credit card information or other payment methods.",
        "• Communications: Any messages, feedback, or inquiries you send to us or other users through the platform."
      ]
    },
    {
      title: "Information We Automatically Collect",
      content: [
        "When you use our online courses platform, we may automatically collect certain information, including:",
        "• Usage Data: Information about how you access and use the platform, such as your IP address, device type, browser type, and operating system.",
        "• Cookies and Tracking Technologies: We use cookies and similar tracking technologies to collect information about your browsing and interaction with our platform. You can manage your cookie preferences through your browser settings."
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "We use the information we collect to provide, maintain, and improve our platform, including personalizing your learning experience, processing payments, communicating with you, and ensuring the security of our services.",
        "Your information helps us develop new features, conduct research, and analyze usage patterns to enhance our educational offerings."
      ]
    },
    {
      title: "Disclosure of Your Information",
      content: [
        "We may share your personal information with the following parties in the following circumstances:",
        "• Instructors and Other Users: We may share your information with instructors and other users as necessary for course-related interactions, such as discussions and assignments.",
        "• Service Providers: We may engage trusted service providers to help us deliver our platform, including hosting, payment processing, and customer support.",
        "• Legal Compliance: We may disclose your information when required by law, legal process, or to protect our rights."
      ]
    },
    {
      title: "Data Security",
      content: [
        "We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, or alteration. However, no data transmission over the internet can be guaranteed to be entirely secure.",
        "We recommend using strong passwords and keeping your login credentials confidential to help protect your account."
      ]
    },
    {
      title: "Your Privacy Rights",
      content: [
        "Depending on your location, you may have certain rights regarding your personal information, including the right to access, correct, or delete your data. You may also have the right to object to or restrict certain processing activities.",
        "To exercise these rights, please contact us using the information provided in the 'Contact Us' section."
      ]
    },
    {
      title: "Children's Privacy",
      content: [
        "Our platform is designed for learners of all ages, but we take special care to protect the privacy of children under 13. We comply with the Children's Online Privacy Protection Act (COPPA) and obtain parental consent when required.",
        "Parents or guardians can review, modify, or delete their child's information by contacting us directly."
      ]
    },
    {
      title: "International Data Transfers",
      content: [
        "If you are accessing our platform from outside the country where our servers are located, your information may be transferred to and maintained on computers located in that country.",
        "By using our platform, you consent to this transfer and processing of your information in accordance with this Privacy Policy."
      ]
    },
    {
      title: "Changes to this Privacy Policy",
      content: [
        "We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. Any updates will be posted on our platform, and the effective date will be revised accordingly.",
        "We encourage you to review this policy periodically to stay informed about how we protect your information."
      ]
    },
    {
      title: "Contact Us",
      content: [
        "If you have any questions, concerns, or requests related to this Privacy Policy or your personal information, please contact us at:",
        <strong key="email">Email: <a href="mailto:support@cloud4coolkids.com">support@cloud4coolkids.com</a></strong>
      ]
    }
  ];

  return (
    <div className="legal-document">
      <motion.div 
        ref={containerRef}
        className="terms-container"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.header 
          className="legal-header"
          variants={fadeInVariants}
          whileHover={{ scale: 1.02 }}
        >
          <motion.h1 
            variants={itemVariants}
            whileHover={{ color: "#2c5282" }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            className="effective-date"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </motion.header>

        <motion.div 
          className="legal-content" 
          variants={containerVariants}
        >
          {sections.map((section, index) => (
            <motion.article 
              key={index} 
              className="legal-section"
              variants={itemVariants}
              whileHover="hover"
              initial="hidden"
              animate={controls}
            >
              <motion.h2
                whileHover={{ x: 5, color: "#2b6cb0" }}
              >
                {section.title}
              </motion.h2>
              <motion.div 
                className="legal-text"
                variants={containerVariants}
              >
                {section.content.map((paragraph, pIndex) => (
                  <motion.p 
                    key={pIndex}
                    variants={itemVariants}
                    whileHover={{ x: 3 }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </motion.div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div 
          className="legal-footer"
          variants={fadeInVariants}
        >
          <motion.p whileHover={{ scale: 1.01 }}>
            For any privacy-related concerns, please don't hesitate to reach out to our support team.
          </motion.p>
          <motion.p 
            className="copyright"
            whileHover={{ scale: 1.02 }}
          >
            © {new Date().getFullYear()} <b>Cloud4CoolKids.</b> All rights reserved.
          </motion.p>
        </motion.div>
      </motion.div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;